import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Tag, Clock, Save, Archive } from "lucide-react";
import { getAdminPricing, setAdminPricing, getRetention, setRetention } from "@/lib/api";

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const PricingPanel = () => {
  const [loading, setLoading] = useState(true);
  const [base, setBase] = useState("15");
  const [saleOn, setSaleOn] = useState(false);
  const [salePrice, setSalePrice] = useState("");
  const [timed, setTimed] = useState(false);
  const [endsAt, setEndsAt] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);

  const [months, setMonths] = useState(6);
  const [savingRet, setSavingRet] = useState(false);

  const load = async () => {
    try {
      const [p, r] = await Promise.all([getAdminPricing(), getRetention()]);
      setBase(String(p.base_price));
      setSaleOn(p.sale_active);
      setSalePrice(p.sale_price != null ? String(p.sale_price) : "");
      setTimed(!!p.sale_ends_at);
      setEndsAt(isoToLocal(p.sale_ends_at));
      setMonths(r.months);
    } catch {
      toast.error("Couldn't load pricing.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrice = async () => {
    setSavingPrice(true);
    try {
      const payload = {
        base_price: Number(base),
        sale_active: saleOn,
        sale_price: saleOn ? Number(salePrice) : null,
        sale_ends_at: saleOn && timed && endsAt ? new Date(endsAt).toISOString() : null,
      };
      const res = await setAdminPricing(payload);
      setSaleOn(res.sale_active);
      toast.success("Pricing saved — it applies immediately.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save pricing.");
    } finally {
      setSavingPrice(false);
    }
  };

  const saveRetention = async () => {
    setSavingRet(true);
    try {
      await setRetention(Number(months));
      toast.success("Retention period saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save retention.");
    } finally {
      setSavingRet(false);
    }
  };

  return (
    <TabsContent value="pricing" className="mt-6 space-y-6" data-testid="pricing-panel">
      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-7 w-7 animate-spin text-clay" /></div>
      ) : (
        <>
          <div className="bg-white border border-border rounded-2xl p-8">
            <div className="flex items-center gap-2 text-ink">
              <Tag className="h-5 w-5 text-clay" />
              <h2 className="font-heading text-xl">Price & sale</h2>
            </div>
            <p className="mt-2 text-sm text-ink-muted max-w-2xl">
              The price shown to customers and the amount charged via Stripe are both read
              server-side at payment time. Changes take effect immediately.
            </p>

            <div className="mt-6 max-w-xs">
              <Label className="text-ink font-medium">Base price (£)</Label>
              <Input
                type="number" min="0" step="0.01"
                data-testid="base-price-input"
                value={base}
                onChange={(e) => setBase(e.target.value)}
                className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
              />
            </div>

            <div className="mt-7 border-t border-border pt-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={saleOn} onCheckedChange={setSaleOn} data-testid="sale-toggle" />
                <span className="font-medium text-ink">Run a sale</span>
              </label>

              {saleOn && (
                <div className="mt-5 space-y-5" data-testid="sale-fields">
                  <div className="max-w-xs">
                    <Label className="text-ink font-medium">Sale price (£) — must be lower than base</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      data-testid="sale-price-input"
                      value={salePrice}
                      onChange={(e) => setSalePrice(e.target.value)}
                      className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
                    />
                  </div>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="saletype" checked={!timed} onChange={() => setTimed(false)} data-testid="sale-open" />
                      <span className="text-sm text-ink">Open-ended (until I turn it off)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="saletype" checked={timed} onChange={() => setTimed(true)} data-testid="sale-timed" />
                      <span className="text-sm text-ink">Timed (auto-ends)</span>
                    </label>
                  </div>
                  {timed && (
                    <div className="max-w-xs">
                      <Label className="text-ink font-medium inline-flex items-center gap-1">
                        <Clock className="h-4 w-4" /> Sale ends at
                      </Label>
                      <Input
                        type="datetime-local"
                        data-testid="sale-end-input"
                        value={endsAt}
                        onChange={(e) => setEndsAt(e.target.value)}
                        className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
                      />
                      <p className="mt-1 text-xs text-ink-faint">Customers see a countdown to this time.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-7 flex justify-end">
              <Button data-testid="save-pricing-button" onClick={savePrice} disabled={savingPrice} className="bg-clay hover:bg-clay-hover text-white rounded-lg px-7 py-5 font-medium">
                {savingPrice ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Save pricing</>)}
              </Button>
            </div>
          </div>

          <div className="bg-white border border-border rounded-2xl p-8">
            <div className="flex items-center gap-2 text-ink">
              <Archive className="h-5 w-5 text-clay" />
              <h2 className="font-heading text-xl">Feedback retention</h2>
            </div>
            <p className="mt-2 text-sm text-ink-muted max-w-2xl">
              Stored feedback entries and their PDFs are auto-deleted once older than this many months.
            </p>
            <div className="mt-5 flex items-end gap-3 max-w-xs">
              <div className="flex-1">
                <Label className="text-ink font-medium">Months</Label>
                <Input
                  type="number" min="1" max="60"
                  data-testid="retention-input"
                  value={months}
                  onChange={(e) => setMonths(e.target.value)}
                  className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
                />
              </div>
              <Button data-testid="save-retention-button" onClick={saveRetention} disabled={savingRet} variant="outline" className="border-border text-ink hover:bg-sand-deep rounded-lg px-6 py-6">
                {savingRet ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </>
      )}
    </TabsContent>
  );
};
